/* The Project Status Report's data, loaded in one place (PSR #14). The report page, the job
   pop-up on Finance → Job Costs and the project's Job Costs tab all export from it, so the
   PDF and the Excel workbook show the same figures whichever way they're reached.

   Figures come from the Job Costs builders (buildFinancials / buildPhases / buildMonthly);
   billing reads CE's invoices, which are right for every job, rather than Summit billing,
   which only a few jobs use. */
import { testDateOf, nowOn } from './projectToday';
import {
  getProject,
  getProjectBySlug,
  getProjectSummary,
  getSyncJobCostCurrentSummary,
  getSyncJobCostPhases,
  getSyncJobCostsCurrent,
  getSyncJobCostMonthlySummary,
  getCostCodeNotes,
  getArJobCollections,
  getJobHoursCurrent,
  getJobHoursMonthly,
  getArInvoicedMonthly,
  getCeChangeOrders,
} from '../api';
import {
  applyBudgetAssumption,
  buildFinancials,
  buildMonthly,
  buildPhases,
  hoursByCode,
  hoursByDiscipline,
  phaseNamesOf,
  travelByCode,
} from '../components/jobcost/JobCostShared';

/* A 'YYYY-MM-DD' (or a DATE serialized as midnight UTC) as a local date — new Date('2026-09-30')
   is UTC midnight, which Central shows as the day before. */
export const localDay = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
};
export const fmtDay = (d) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
export const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/* Where the printable report lives (the PDF, Print and Email report are all there). */
export const psrReportPath = (project) => `/projects/${encodeURIComponent(project.slug || project.id)}/report`;

// The app's own font link stops at Geist Mono 600; the report's figures are 800.
const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=Geist+Mono:wght@400;500;600;700;800&display=swap';
const FONT_FACES = ["400 13px 'Geist'", "500 12px 'Geist'", "600 12px 'Geist'", "700 12px 'Geist'", "400 12px 'Geist Mono'", "600 12px 'Geist Mono'", "800 22px 'Geist Mono'"];

/* Fonts first: the report measures its pages in them, and the workbook's two charts are drawn
   in them. The stylesheet has to load before document.fonts knows the faces exist. Never waits
   past 5s — a fallback face is better than no report. */
export const loadPsrFonts = () => new Promise((resolve) => {
  const fonts = document.fonts;
  const go = () => {
    if (!fonts) { resolve(); return; }
    Promise.all(FONT_FACES.map((f) => fonts.load(f))).then(() => fonts.ready).catch(() => {}).then(resolve);
  };
  setTimeout(resolve, 5000);
  let link = document.getElementById('psr-fonts');
  if (link && link.sheet) { go(); return; }
  if (!link) {
    link = document.createElement('link');
    link.id = 'psr-fonts';
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    document.head.appendChild(link);
  }
  link.addEventListener('load', go, { once: true });
  link.addEventListener('error', go, { once: true });
});

/* Everything the report shows, for one project (its id or slug). The report page adds its own
   page-layout sizes on top; the workbook uses it as it is. */
export async function loadPsrData(idOrSlug) {
  const project = /^\d+$/.test(String(idOrSlug)) ? await getProject(parseInt(idOrSlug, 10)) : await getProjectBySlug(idOrSlug);
  const job = project.ce_job_id || project.project_number;
  const soft = (p, fallback) => p.catch(() => fallback);
  const [totals, rawPhases, items, trend, notes, collections, hoursRows, hoursTrend, invoiced, cos, summary] = await Promise.all([
    getSyncJobCostCurrentSummary({ job_id: job }),
    getSyncJobCostPhases({ job_id: job, source: 'current' }),
    getSyncJobCostsCurrent(job),
    getSyncJobCostMonthlySummary(job),
    soft(getCostCodeNotes(job), []),
    soft(getArJobCollections(job), null),
    soft(getJobHoursCurrent(job), []),
    soft(getJobHoursMonthly(job), null),
    soft(getArInvoicedMonthly(job), null),
    soft(getCeChangeOrders(job), []),
    soft(getProjectSummary(project.id), null),
  ]);
  const t = (totals || [])[0] || null;
  const fin = buildFinancials(t, project);
  const phases = buildPhases(rawPhases, applyBudgetAssumption(items || [], job)).map((p) => ({ ...p, actual: p.actual_cost }));
  const sum = (rows, k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
  const detailRows = [];
  const codeName = {};
  const phaseName = {};
  phases.forEach((p) => {
    phaseName[String(p.no).trim()] = p.name;
    detailRows.push({ kind: 'phase', code: p.no, name: p.name, budget: p.budget, actual: p.actual, committed: p.committed });
    (p.codes || []).forEach((c) => {
      codeName[`${String(p.no).trim()}|${String(c.code).trim()}`] = c.cat;
      detailRows.push({ kind: 'code', code: c.code, name: c.cat, budget: c.budget, actual: c.actual_cost, committed: c.committed, assumed: c.assumed });
    });
  });
  const phaseTotal = { budget: sum(phases, 'budget'), actual: sum(phases, 'actual'), committed: sum(phases, 'committed') };
  detailRows.push({ kind: 'total', ...phaseTotal });
  const travel = travelByCode(phases);
  const hours = hoursByCode(hoursRows || []);
  // The export's hours table, grouped by discipline like the Labor hours drop-down (#62).
  const hoursGroups = hoursByDiscipline(hoursRows || [], phaseNamesOf(phases));
  const coRows = Array.isArray(cos) ? cos : [];
  return {
    project,
    job: project.project_number || project.ce_job_id || '',
    fin,
    phases,
    phaseTotal,
    detailRows,
    chart: buildMonthly(trend, hoursTrend, invoiced).map((x) => {
      const [y, mo] = String(x.key).split('-').map(Number);
      return { y, m: mo, spent: Math.max(0, x.cost || 0), billed: Math.max(0, x.billed || 0), hours: x.hours == null ? null : Math.max(0, x.hours) };
    }),
    collections,
    travel,
    travelTotal: { budget: sum(travel, 'budget'), used: sum(travel, 'used') },
    hours,
    hoursGroups,
    hoursTotal: { budget: sum(hours, 'budget'), used: sum(hours, 'used') },
    cos: coRows,
    coTotal: sum(coRows, 'contract_change'),
    notes: Array.isArray(notes) ? notes : [],
    codeName,
    phaseName,
    // The schedule's first and last days, else the project's Start and End dates —
    // the same dates, in the same order, as the project header.
    started: localDay((summary && summary.earliest_task_start) || project.start_date),
    endDate: (summary && summary.latest_task_finish) || project.end_date || null,
    asOf: localDay(t && t.report_date),
    // A test job's report is generated as of its test date (2026-10-20).
    testDate: testDateOf(project),
    today: ymd(nowOn(testDateOf(project))),
    generated: fmtDay(nowOn(testDateOf(project))),
  };
}

/* Build the workbook (lib/psrXlsx, ExcelJS — loaded only now) and hand it to the browser. */
export async function downloadPsrWorkbook(d) {
  await loadPsrFonts(); // the workbook's two charts are drawn in Geist
  const { buildPsrXlsx } = await import('./psrXlsx');
  const buf = await buildPsrXlsx(d);
  const url = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `PSR ${d.job || 'report'} ${d.today || ymd(new Date())}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/* From anywhere that knows the project: load the report's data, then download the workbook. */
export async function exportPsrWorkbook(project) {
  await downloadPsrWorkbook(await loadPsrData(project.slug || project.id));
}
